import type { Paise } from '@/shared/utils/money';

/** Cart line quantity accepted by the staff offline-order API. */
export const OFFLINE_MAX_QUANTITY = 999;

export type OfflineProductSortBy = 'relevance' | 'name' | 'price';
export type OfflineSortOrder = 'asc' | 'desc';

export interface OfflineProductSearchParams {
  storeId: string;
  query?: string;
  categoryId?: string;
  brandId?: string;
  tags?: string;
  /** Rupees. Sent only when the caller sets a bound. */
  minPrice?: number;
  /** Rupees. Sent only when the caller sets a bound. */
  maxPrice?: number;
  inStockOnly?: boolean;
  onOfferOnly?: boolean;
  sortBy?: OfflineProductSortBy;
  sortOrder?: OfflineSortOrder;
  page?: number;
  limit?: number;
}

/** Search hit. Prices are integer paise converted at the API boundary. */
export interface OfflineProduct {
  productId: string;
  name: string;
  unit: string;
  primaryImageUrl: string;
  mrpPaise: Paise;
  /** Chargeable price before promo. */
  normalSellingPricePaise: Paise;
  /** App / promo display price. */
  customerSellingPricePaise: Paise;
  quantity: number;
  inStock: boolean;
}

export interface OfflineProductSearchResult {
  items: OfflineProduct[];
  total: number;
  page: number;
  limit: number;
  pageCount: number;
}

export interface OfflineCartItem {
  productId: string;
  productName: string;
  productImageUrl: string;
  unitPricePaise: Paise;
  baseUnitPricePaise: Paise;
  mrpPaise: Paise;
  quantity: number;
  unit: string;
  lineDiscountPaise: Paise;
  promotionName: string | null;
  hasPromotion: boolean;
}

export interface OfflineCart {
  userId: string;
  storeId: string;
  items: OfflineCartItem[];
  subtotalPaise: Paise;
  handlingChargePaise: Paise;
  deliveryChargePaise: Paise;
  payableTotalPaise: Paise;
  discountPaise: Paise;
  /**
   * Pre-coupon payable in rupees, as returned by the server.
   * Coupon list `cartValue` must stay in rupees — do not round-trip through paise.
   */
  cartValueRupees: number;
}

/** Shared cart payload after add, update, remove, identify, clear-customer, coupon apply/remove. */
export interface OfflineSaleSession {
  cart: OfflineCart;
  customerId: string;
  customerPhoneNumber: string;
  customerName: string;
  customerRegistered: boolean;
  couponDiscountPaise: Paise;
  /** Payable amount. This is what the screen charges. */
  totalAmountPaise: Paise;
  totalAmountRupees: number;
  couponDiscountRupees: number;
}

export interface OfflineCustomerSnapshot {
  found: boolean;
  registered: boolean;
  fullName: string;
  phoneNumber: string;
  email: string;
  status: string;
}

export interface OfflineCoupon {
  id: string;
  code: string;
  description: string;
  type: string;
  discountKind: string;
  /** Paise when flat; percentage points when `discountKind` is PERCENTAGE. */
  discountValue: number;
  maxDiscountAmountPaise: Paise | null;
  minOrderValuePaise: Paise;
  startsAt: string | null;
  endsAt: string | null;
  isActive: boolean;
}

export interface OfflineCouponList {
  items: OfflineCoupon[];
  total: number;
  page: number;
  limit: number;
  pageCount: number;
}

export interface OfflineCouponPreview {
  valid: boolean;
  message: string;
  discountPaise: Paise;
  totalAfterDiscountPaise: Paise;
}

export type OfflineFulfillmentType = 'IN_STORE' | 'HOME_DELIVERY';

export const OFFLINE_ADDRESS_MAX = {
  line1: 255,
  line2: 255,
  city: 120,
  state: 120,
  pincode: 12,
  label: 50,
} as const;

export interface OfflineCheckoutDeliveryAddress {
  line1?: string;
  line2?: string;
  city?: string;
  state?: string;
  pincode?: string;
  latitude?: number;
  longitude?: number;
  label?: string;
  phoneNumber?: string;
  customerName?: string;
}

/** Local draft for optional home-delivery address fields. Coords stay strings until checkout. */
export interface OfflineAddressDraft {
  line1: string;
  line2: string;
  city: string;
  state: string;
  pincode: string;
  label: string;
  latitude: string;
  longitude: string;
}

export function emptyOfflineAddressDraft(): OfflineAddressDraft {
  return {
    line1: '',
    line2: '',
    city: '',
    state: '',
    pincode: '',
    label: '',
    latitude: '',
    longitude: '',
  };
}

export interface OfflineCheckoutInput {
  storeId: string;
  customerName?: string;
  phoneNumber?: string;
  idempotencyKey: string;
  fulfillmentType?: OfflineFulfillmentType;
  deliveryAddress?: OfflineCheckoutDeliveryAddress;
}

export interface OfflineCheckoutResult {
  orderId: string;
  orderNumber: string;
  status: string;
  totalAmountPaise: Paise;
  message: string;
  currency: string;
  paymentStatus: string;
}

export interface OfflineSaleBillLine {
  productId: string;
  name: string;
  quantity: number;
  unit: string;
  unitPricePaise: Paise;
  lineTotalPaise: Paise;
  mrpPaise: Paise;
}

/** Frozen at checkout, before the cart is cleared, so the bill can be printed. */
export interface OfflineSaleBill {
  orderId: string;
  orderNumber: string;
  status: string;
  paymentStatus: string;
  message: string;
  /** ISO timestamp captured when the sale succeeded. */
  soldAt: string;
  storeCode: string;
  storeName: string;
  storeCity: string;
  customerName: string;
  customerPhone: string;
  couponCode: string;
  lines: OfflineSaleBillLine[];
  subtotalPaise: Paise;
  handlingChargePaise: Paise;
  deliveryChargePaise: Paise;
  discountPaise: Paise;
  couponDiscountPaise: Paise;
  totalAmountPaise: Paise;
  fulfillmentType: OfflineFulfillmentType;
  addressLines: string[];
}

export function emptyOfflineCart(): OfflineCart {
  return {
    userId: '',
    storeId: '',
    items: [],
    subtotalPaise: 0,
    handlingChargePaise: 0,
    deliveryChargePaise: 0,
    payableTotalPaise: 0,
    discountPaise: 0,
    cartValueRupees: 0,
  };
}

export function emptyOfflineSession(): OfflineSaleSession {
  return {
    cart: emptyOfflineCart(),
    customerId: '',
    customerPhoneNumber: '',
    customerName: '',
    customerRegistered: false,
    couponDiscountPaise: 0,
    totalAmountPaise: 0,
    totalAmountRupees: 0,
    couponDiscountRupees: 0,
  };
}
